/**
 * The project document — what an organization shares (§5.25).
 *
 * It is a workspace with everything that belongs to one machine taken out:
 * repositories carry their remote instead of their folder, secrets carry their
 * name instead of their value, and nothing carries a row's timestamps. What is
 * left is everything a new member would otherwise type — the repositories and
 * how to run them, the whole workspace configuration, the API console, and
 * which ticket projects feed the board.
 *
 * **Workflows are not in it.** They belong to no workspace: an install keeps
 * one list for every workspace it holds, and an organization shares one list
 * for all of its workspaces — `OrgWorkflows`, below, a document of its own with
 * its own versions. `workflows` stays on this one, always empty, because an
 * app installed before that reads the key without asking whether it is there.
 *
 * **Ids travel.** A repository, a request, a flow, a workflow keeps the id it
 * was given where it was first written, on every machine that holds a copy. That
 * is what a three-way merge is keyed on (`merge.ts`), and what the references
 * inside the document already assume: a flow's node names a request by id, a
 * request names its flow by id.
 *
 * **Secret values never do.** An environment variable or a console variable
 * marked secret is here with `value: null` — its value is sealed separately in
 * the account area, under a path `secrets.ts` spells, and a member's install
 * seals it again in its own keychain.
 *
 * Validation is `parse.ts`, and it is the only way a document is made from
 * anything that crossed a network.
 */
import type { ApiFlowInput, ApiRequestInput } from '../api/types';
import type { WorkspaceConfig } from '../settings/types';
import type { WorkflowInput } from '../workflow/types';
import type {
  EnvFileMode,
  EnvScope,
  LaneStatuses,
  ProjectKind,
  RuntimeKind,
  TaskSourceKind
} from './vocabulary';
import type { RepoHost } from './remote';

/** Raised when the shape changes in a way an older app would misread. */
export const WORKSPACE_SCHEMA = 1;

/** One megabyte, measured on the JSON: past that it is not a configuration, it is a dump. */
export const MAX_DOCUMENT_BYTES = 1024 * 1024;

/** More repositories than a workspace is picked with at once (§15). */
export const MAX_PROJECTS = 64;
/** Per repository. The console's own ceiling on variables is the same number. */
export const MAX_ENV_VARS = 200;
export const MAX_TASK_SOURCES = 20;

export const MAX_WORKSPACE_NAME_CHARS = 80;
export const MAX_PROJECT_NAME_CHARS = 80;
export const MAX_REL_PATH_CHARS = 200;
/** A command is a line somebody types; a script belongs in the repository. */
export const MAX_COMMAND_CHARS = 2000;
export const MAX_ENV_KEY_CHARS = 200;
export const MAX_ENV_VALUE_CHARS = 32_000;

/** The id every row of the desktop app is minted with, and so every id here. */
export const ROW_ID = /^[A-Za-z0-9_-]{1,40}$/;

export interface DocEnvVar {
  key: string;
  /** `null` exactly when `isSecret` — the value is sealed apart from the document. */
  value: string | null;
  isSecret: boolean;
  /** Always `both` on the way out; kept because an older app's parser requires it (`EnvScope`). */
  scope: EnvScope;
}

export interface DocApiFolder {
  id: string;
  parentId: string | null;
  name: string;
  sortOrder: number;
}

export interface DocApiRequest extends ApiRequestInput {
  id: string;
  folderId: string | null;
  /** Deliberately not checked against the flows: a gone flow reads as "sent directly" (§5.11). */
  flowId: string | null;
  name: string;
  sortOrder: number;
  /**
   * The call's documentation — how an organization shares what its APIs are
   * for (2026-09-30): an OpenAPI document in YAML since 2026-10-02, markdown
   * before; text either way, which is all this validator reads. Only where
   * there is some, so a document without any still reads in a copy of the app
   * that predates the key (`subPath`'s bargain).
   */
  doc?: string;
  /*
   * `scripts` comes with `ApiRequestInput`, on the same bargain: only where a
   * call has one (2026-10-01), so a copy of the app that predates the key still
   * reads every document whose calls have none.
   */
}

/** How long one call's documentation may be. */
export const MAX_REQUEST_DOC_CHARS = 64_000;

export interface DocApiEnvironment {
  id: string;
  name: string;
  sortOrder: number;
}

export interface DocApiVariable {
  id: string;
  key: string;
  /** `null` when secret; a secret's per-environment values are sealed too, so `values` is `{}`. */
  value: string | null;
  values: Record<string, string>;
  isSecret: boolean;
  sortOrder: number;
}

export interface DocApiFlow extends ApiFlowInput {
  id: string;
  name: string;
  sortOrder: number;
}

/** A repository's API console, whole (§5.11). */
export interface DocApi {
  folders: DocApiFolder[];
  requests: DocApiRequest[];
  environments: DocApiEnvironment[];
  variables: DocApiVariable[];
  flows: DocApiFlow[];
}

export interface DocProject {
  id: string;
  name: string;
  /** The identity (`remote.ts`): what a member's existing checkout is matched on. */
  remote: string;
  host: RepoHost;
  /** What a member without a checkout clones. Never carries a credential. */
  cloneUrl: string;
  /** Where it goes under the folder a member picks, `/`-separated whatever the OS. */
  relPath: string;
  /**
   * The folder of its repository this project runs in, when it is one of
   * several projects in one repository (§5.4) — `site`, `apps/web`. Absent is
   * the repository itself. A project with one is cloned with the entry that
   * has the same `remote` and no `subPath`, never on its own, and shares its
   * `cloneUrl`, `relPath` and `defaultBranch`.
   *
   * Written only where it is set: a document without one reads in every
   * installed Airlock, and one with it asks an older copy to update (`parse.ts`
   * refuses a field it does not know).
   */
  subPath?: string;
  defaultBranch: string;
  runtime: RuntimeKind;
  kind: ProjectKind;
  packageManager: string | null;
  installCmd: string | null;
  startCmd: string | null;
  checkCmd: string | null;
  healthCheck: string | null;
  port: string | null;
  portEnv: string | null;
  tunnelDomain: string | null;
  tunnelAuto: boolean;
  /**
   * Off: a boot always runs this project's install, even over a tree that is
   * exactly what its build files describe (§8.2). Absent is on, and only an
   * off is ever written — `subPath`'s bargain, so a document that never turns
   * it off still reads in a copy of the app that predates the key.
   */
  skipUnchangedInstall?: false;
  color: string;
  envFile: EnvFileMode;
  sortOrder: number;
  env: DocEnvVar[];
  api: DocApi;
}

export interface DocWorkflow extends WorkflowInput {
  id: string;
  name: string;
  sortOrder: number;
}

/**
 * An organization's workflows (§5.25) — one document per organization, common
 * to every workspace it shares, versioned the way its settings are.
 */
export interface OrgWorkflows {
  workflows: DocWorkflow[];
}

export const EMPTY_ORG_WORKFLOWS: OrgWorkflows = { workflows: [] };

/** One ticket project routed to this workspace (§5.21). The credential is each member's own. */
export interface DocTaskSource {
  id: string;
  kind: TaskSourceKind;
  host: string;
  projectKey: string;
  doneStatus: string;
  importStatuses: string[];
  laneStatuses: LaneStatuses;
}

/** Every override, every egress list and every host service — the workspace's whole configuration. */
export type DocConfig = Omit<WorkspaceConfig, 'workspaceId'>;

export interface WorkspaceDocument {
  schema: typeof WORKSPACE_SCHEMA;
  name: string;
  color: string;
  repos: DocProject[];
  config: DocConfig;
  /** @deprecated Always empty — see the note on workflows above. */
  workflows: DocWorkflow[];
  taskSources: DocTaskSource[];
}
